import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Plus, Calendar, Clock, AlertTriangle, CheckCircle2, Sparkles, FileBox, Lightbulb, CheckSquare, Send, Beaker } from 'lucide-react';
function daysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}
interface RiskSignal {
  id: string;
  label: string;
  icon: React.ElementType;
  status: 'at-risk' | 'needs-attention' | 'on-track';
  summary: string;
  detail: string;
}
const statusStyles = {
  'at-risk': {
    dot: 'bg-red-500',
    badge: 'bg-red-50 text-red-700 border-0',
    label: 'At Risk'
  },
  'needs-attention': {
    dot: 'bg-amber-500',
    badge: 'bg-amber-50 text-amber-700 border-0',
    label: 'Needs Attention'
  },
  'on-track': {
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 border-0',
    label: 'On Track'
  }
};
export function DashboardPage() {
  const navigate = useNavigate();
  const hasNewProposal = localStorage.getItem('newProposalCreated') === 'true';
  const newProposalTitle = localStorage.getItem('newProposalTitle') || 'Multisensory Study';
  const newProposalReadyCount = parseInt(localStorage.getItem('artifactsReady_10') || '0', 10);
  const newProposalTotal = parseInt(localStorage.getItem('artifactsTotal_10') || '18', 10);
  const proposals = [{
    id: '1',
    title: "NIH R01: Neural Mechanisms of Alzheimer's Disease Progression",
    funder: 'NIH / NIA',
    mechanism: 'R01',
    status: 'In Progress',
    progress: 42,
    artifactsCompleted: 9,
    artifactsTotal: 19,
    deadline: 'Mar 5, 2026',
    risk: 'At Risk' as const,
    riskSummary: `At risk due to 1 overdue milestone, 2 overdue tasks, and 2 unsatisfied criteria with only ${daysUntil('2026-03-05')} days remaining at 42% completion.`,
    riskSignals: [{
      id: 'timeline',
      label: 'Timeline',
      icon: Clock,
      status: 'at-risk' as const,
      summary: '1 milestone overdue (Budget draft to SPO)',
      detail: `${daysUntil('2026-03-05')} days left at 42% complete`
    }, {
      id: 'artifacts',
      label: 'Documents',
      icon: FileBox,
      status: 'on-track' as const,
      summary: '12 of 15 documents uploaded',
      detail: 'Remaining 3 not yet due'
    }, {
      id: 'criteria',
      label: 'Criteria',
      icon: Lightbulb,
      status: 'needs-attention' as const,
      summary: '2 not satisfied, 1 unknown of 13 criteria',
      detail: 'Innovation overlap · Letters of support incomplete'
    }, {
      id: 'tasks',
      label: 'Tasks',
      icon: CheckSquare,
      status: 'at-risk' as const,
      summary: '2 overdue tasks',
      detail: 'Letter of support (Jan 3) · UMich F&A rate (Jan 5)'
    }] as RiskSignal[],
    team: [{
      initials: 'SC',
      color: 'bg-indigo-100 text-indigo-700'
    }, {
      initials: 'MS',
      color: 'bg-emerald-100 text-emerald-700'
    }, {
      initials: 'JP',
      color: 'bg-amber-100 text-amber-700'
    }]
  }, {
    id: '2',
    title: 'NSF CAREER: Quantum Computing Education for High Schools',
    funder: 'NSF',
    mechanism: 'CAREER',
    status: 'Submitted',
    progress: 100,
    artifactsCompleted: 14,
    artifactsTotal: 14,
    deadline: 'Submitted Feb 15, 2026',
    risk: 'Submitted' as const,
    riskSummary: 'Application submitted successfully. Awaiting review panel assignment.',
    riskSignals: [{
      id: 'timeline',
      label: 'Timeline',
      icon: Clock,
      status: 'on-track' as const,
      summary: 'Submitted on time',
      detail: 'Feb 15, 2026 — 2 days before deadline'
    }, {
      id: 'artifacts',
      label: 'Artifacts',
      icon: FileBox,
      status: 'on-track' as const,
      summary: 'All 14 artifacts uploaded',
      detail: 'All compliance checks passed'
    }, {
      id: 'criteria',
      label: 'Criteria',
      icon: Lightbulb,
      status: 'on-track' as const,
      summary: 'All 9 criteria satisfied',
      detail: 'No outstanding issues'
    }, {
      id: 'tasks',
      label: 'Tasks',
      icon: CheckSquare,
      status: 'on-track' as const,
      summary: 'All tasks completed',
      detail: '12 of 12 tasks done'
    }] as RiskSignal[],
    team: [{
      initials: 'SC',
      color: 'bg-indigo-100 text-indigo-700'
    }, {
      initials: 'AL',
      color: 'bg-blue-100 text-blue-700'
    }]
  }, {
    id: '3',
    title: 'Gates Foundation: Equitable Access to mRNA Vaccine Manufacturing',
    funder: 'Gates Foundation',
    mechanism: 'Grand Challenges',
    status: 'In Progress',
    progress: 68,
    artifactsCompleted: 12,
    artifactsTotal: 15,
    deadline: 'Jun 15, 2026',
    risk: 'On Track' as const,
    riskSummary: `Progressing well at 68% completion with ${daysUntil('2026-06-15')} days remaining. All milestones on schedule and no overdue tasks.`,
    riskSignals: [{
      id: 'timeline',
      label: 'Timeline',
      icon: Clock,
      status: 'on-track' as const,
      summary: 'All milestones on schedule',
      detail: `${daysUntil('2026-06-15')} days remaining at 68% complete`
    }, {
      id: 'artifacts',
      label: 'Artifacts',
      icon: FileBox,
      status: 'on-track' as const,
      summary: '12 of 15 artifacts uploaded',
      detail: 'Remaining 3 not yet due'
    }, {
      id: 'criteria',
      label: 'Criteria',
      icon: Lightbulb,
      status: 'on-track' as const,
      summary: '10 of 11 criteria satisfied',
      detail: '1 pending final review'
    }, {
      id: 'tasks',
      label: 'Tasks',
      icon: CheckSquare,
      status: 'on-track' as const,
      summary: '2 tasks remaining',
      detail: 'Both on schedule, due next week'
    }] as RiskSignal[],
    team: [{
      initials: 'SC',
      color: 'bg-indigo-100 text-indigo-700'
    }, {
      initials: 'RK',
      color: 'bg-rose-100 text-rose-700'
    }, {
      initials: 'TY',
      color: 'bg-cyan-100 text-cyan-700'
    }]
  }];
  const renderStatusBadge = (proposal: (typeof proposals)[0]) => {
    if (proposal.risk === 'At Risk') {
      return <Badge className="bg-red-100 hover:bg-red-100 text-red-800 border border-red-300 text-[11px] font-bold px-2 py-0.5 h-auto gap-1 shadow-sm" data-id="element-939">
          <AlertTriangle className="h-3 w-3" data-id="element-940" />
          At Risk
          <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[9px] px-1 py-0 h-3.5 gap-0.5 shadow-none ml-0.5 font-semibold rounded-full" data-id="element-941">
            <Sparkles className="h-2 w-2" data-id="element-942" />
            AI
          </Badge>
        </Badge>;
    }
    if (proposal.risk === 'Submitted') {
      return <Badge className="bg-blue-100 hover:bg-blue-100 text-blue-800 border border-blue-300 text-[11px] font-bold px-2 py-0.5 h-auto gap-1 shadow-sm" data-id="element-943">
          <Send className="h-3 w-3" data-id="element-944" />
          Submitted
        </Badge>;
    }
    if (proposal.risk === 'On Track') {
      return <Badge className="bg-emerald-100 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 text-[11px] font-bold px-2 py-0.5 h-auto gap-1 shadow-sm" data-id="element-945">
          <CheckCircle2 className="h-3 w-3" data-id="element-946" />
          On Track
          <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[9px] px-1 py-0 h-3.5 gap-0.5 shadow-none ml-0.5 font-semibold rounded-full" data-id="element-947">
            <Sparkles className="h-2 w-2" data-id="element-948" />
            AI
          </Badge>
        </Badge>;
    }
    return null;
  };
  return <div className="min-h-full bg-muted/40 p-6 sm:p-10" data-id="element-949">
      <div className="max-w-6xl mx-auto space-y-8" data-id="element-950">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-951">
          <div data-id="element-952">
            <h1 className="text-2xl font-bold text-foreground tracking-tight" data-id="element-953">
              My Proposals
            </h1>
            <p className="text-muted-foreground text-sm mt-1" data-id="element-954">
              Manage your active grants and applications.
            </p>
          </div>
          <Button onClick={() => navigate('/proposals/new')} className="bg-indigo-600 hover:bg-indigo-700 gap-2" data-id="element-955">
            <Plus className="h-4 w-4" data-id="element-956" />
            New Proposal
          </Button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-957">
          {hasNewProposal && <Card className="group cursor-pointer hover:shadow-md transition-all border-border/60 hover:border-indigo-200" onClick={() => navigate('/proposals/10/timeline')} data-id="element-958">
              <CardContent className="p-6 space-y-4" data-id="element-959">
                {/* Top Row: Funder badge (left) + Status badge (right) */}
                <div className="flex items-start justify-between gap-2" data-id="element-960">
                  <Badge variant="outline" className="bg-white text-xs font-medium" data-id="element-961">
                    NIH
                  </Badge>
                  <Badge className="bg-emerald-100 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 text-[11px] font-bold px-2 py-0.5 h-auto gap-1 shadow-sm" data-id="element-962">
                    <CheckCircle2 className="h-3 w-3" data-id="element-963" />
                    On Track
                    <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[9px] px-1 py-0 h-3.5 gap-0.5 shadow-none ml-0.5 font-semibold rounded-full" data-id="element-964">
                      <Sparkles className="h-2 w-2" data-id="element-965" />
                      AI
                    </Badge>
                  </Badge>
                </div>

                {/* Title + Mechanism */}
                <div data-id="element-966">
                  <h3 className="font-semibold text-foreground leading-snug group-hover:text-indigo-700 transition-colors" data-id="element-967">
                    NIH R21: {newProposalTitle}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground" data-id="element-968">
                    <Beaker className="h-3.5 w-3.5 flex-shrink-0" data-id="element-969" />
                    <span data-id="element-970">R21</span>
                  </div>
                </div>

                {/* Progress */}
                <div className="space-y-1.5" data-id="element-971">
                  <div className="flex items-center justify-between text-xs" data-id="element-972">
                    <span className="font-medium text-foreground flex items-center gap-1.5" data-id="element-973">
                      <span className="h-2 w-2 rounded-full flex-shrink-0 bg-amber-500" data-id="element-974" />
                      In Progress
                    </span>
                    <span className="font-bold text-foreground" data-id="element-975">
                      {newProposalReadyCount} / {newProposalTotal}
                    </span>
                  </div>
                  <Progress value={newProposalTotal > 0 ? Math.round(newProposalReadyCount / newProposalTotal * 100) : 0} className="h-1.5 bg-slate-100" data-id="element-976" />
                </div>

                {/* Footer: Team + Deadline */}
                <div className="pt-4 border-t border-border/50 flex items-center justify-between" data-id="element-977">
                  <div className="flex -space-x-2" data-id="element-978">
                    <div className="h-6 w-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold bg-indigo-100 text-indigo-700" data-id="element-979">
                      SC
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-id="element-980">
                    <Calendar className="h-3.5 w-3.5" data-id="element-981" />
                    <span data-id="element-982">Jun 1, 2026</span>
                  </div>
                </div>
              </CardContent>
            </Card>}

          {proposals.map(proposal => <Card key={proposal.id} className="group cursor-pointer hover:shadow-md transition-all border-border/60 hover:border-indigo-200" onClick={() => navigate(`/proposals/${proposal.id}/timeline`)} data-id="element-983">
              <CardContent className="p-6 space-y-4" data-id="element-984">
                {/* Top Row: Funder badge (left) + Status badge (right) */}
                <div className="flex items-start justify-between gap-2" data-id="element-985">
                  <Badge variant="outline" className="bg-white text-xs font-medium" data-id="element-986">
                    {proposal.funder}
                  </Badge>
                  <div className="flex-shrink-0" data-id="element-987">
                    {renderStatusBadge(proposal)}
                  </div>
                </div>

                {/* Title + Mechanism */}
                <div data-id="element-988">
                  <h3 className="font-semibold text-foreground leading-snug group-hover:text-indigo-700 transition-colors" data-id="element-989">
                    {proposal.title}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground" data-id="element-990">
                    <Beaker className="h-3.5 w-3.5 flex-shrink-0" data-id="element-991" />
                    <span data-id="element-992">{proposal.mechanism}</span>
                  </div>
                </div>

                {/* Progress */}
                <div className="space-y-1.5" data-id="element-993">
                  <div className="flex items-center justify-between text-xs" data-id="element-994">
                    <span className="font-medium text-foreground flex items-center gap-1.5" data-id="element-995">
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${proposal.status === 'Submitted' ? 'bg-blue-500' : proposal.status === 'Under Review' ? 'bg-purple-500' : proposal.status === 'Needs Review' ? 'bg-emerald-500' : proposal.status === 'Draft' ? 'bg-slate-400' : 'bg-amber-500'}`} data-id="element-996" />
                      {proposal.status}
                    </span>
                    <span className="text-foreground font-[500]" data-id="element-997">
                      {proposal.artifactsCompleted} / {proposal.artifactsTotal}
                    </span>
                  </div>
                  <Progress value={proposal.artifactsTotal > 0 ? Math.round(proposal.artifactsCompleted / proposal.artifactsTotal * 100) : 0} className="h-1.5 bg-slate-100" data-id="element-998" />
                </div>

                {/* Footer: Team + Deadline */}
                <div className="pt-4 border-t border-border/50 flex items-center justify-between" data-id="element-999">
                  <div className="flex -space-x-2" data-id="element-1000">
                    {proposal.team.map((member, i) => <div key={i} className={`h-6 w-6 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold ${member.color}`} data-id="element-1001">
                        {member.initials}
                      </div>)}
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-id="element-1002">
                    <Calendar className="h-3.5 w-3.5" data-id="element-1003" />
                    <span data-id="element-1004">{proposal.deadline}</span>
                  </div>
                </div>
              </CardContent>
            </Card>)}

          {/* New Proposal Card (Placeholder style) */}
          <button onClick={() => navigate('/proposals/new')} className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-slate-50/50 hover:bg-slate-50 hover:border-indigo-300 transition-all h-full min-h-[200px] group" data-id="element-1005">
            <div className="h-10 w-10 rounded-full bg-white border shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1006">
              <Plus className="h-5 w-5 text-indigo-600" data-id="element-1007" />
            </div>
            <span className="text-sm font-medium text-muted-foreground group-hover:text-indigo-700" data-id="element-1008">
              Create New Proposal
            </span>
          </button>
        </div>
      </div>
    </div>;
}